'use client';

import type { ReactNode } from 'react';
import { AcademicCapIcon, BuildingOffice2Icon, CalendarDaysIcon, MapPinIcon, TruckIcon, UserGroupIcon, BeakerIcon } from '@heroicons/react/24/outline';
import { ScheduleResultsRenderer } from '@/app/Components/Assistant/renderers/ScheduleResultsRenderer';

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function text(value: unknown): string {
  return typeof value === 'string' && value.trim() ? value.trim() : '';
}

function list(value: unknown, keys: string[]): Array<Record<string, unknown>> {
  const data = record(value);
  if (!data) return [];
  for (const key of keys) {
    const candidate = data[key];
    if (Array.isArray(candidate)) return candidate.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object' && !Array.isArray(item));
  }
  return [];
}

function ResultCard({ eyebrow, title, href, hrefLabel, children }: { eyebrow: string; title: string; href: string; hrefLabel: string; children: ReactNode }) {
  return (
    <section className="overflow-hidden rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface)]">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-[var(--ss-border)] px-4 py-3">
        <div>
          <p className="operational-eyebrow">{eyebrow}</p>
          <h3 className="mt-1 font-semibold text-[var(--ss-text)]">{title}</h3>
        </div>
        <a href={href} className="inline-flex min-h-12 items-center rounded-control border border-[var(--ss-border)] px-4 font-semibold text-[var(--ss-brand)] hover:bg-[var(--ss-brand-bg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]">{hrefLabel}</a>
      </header>
      <div className="divide-y divide-[var(--ss-border)]">{children}</div>
    </section>
  );
}

function ResultRow({ icon, title, detail }: { icon: ReactNode; title: string; detail: string }) {
  return (
    <div className="flex min-h-16 items-start gap-3 px-4 py-3">
      <span className="grid size-10 shrink-0 place-items-center rounded-control bg-[var(--ss-ai-bg)] text-[var(--ss-ai)]">{icon}</span>
      <div className="min-w-0">
        <p className="font-semibold text-[var(--ss-text)]">{title}</p>
        {detail ? <p className="mt-1 text-xs leading-5 text-[var(--ss-text-muted)]">{detail}</p> : null}
      </div>
    </div>
  );
}

export function AssistantScheduleVisual({ schedules }: { schedules: unknown[] }) {
  if (!schedules.length) return null;
  return (
    <div className="overflow-hidden rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface)] p-3">
      <ScheduleResultsRenderer data={{ schedules }} metadata={{ source: 'Generated schedule options' }} />
    </div>
  );
}

export function AssistantDegreeVisual({ data }: { data: Record<string, unknown> | null }) {
  const degrees = list(data, ['degrees']);
  if (degrees.length) {
    return (
      <ResultCard eyebrow="Degree catalog" title={`${degrees.length} program${degrees.length === 1 ? '' : 's'} found`} href="/explore?view=degrees" hrefLabel="Open Degree Explorer">
        {degrees.slice(0, 6).map((degree, index) => (
          <ResultRow
            key={String(degree.id ?? index)}
            icon={<AcademicCapIcon className="size-5" aria-hidden="true" />}
            title={text(degree.name) || 'Rutgers program'}
            detail={[text(degree.code), text(degree.startTerm)].filter(Boolean).join(' · ')}
          />
        ))}
      </ResultCard>
    );
  }
  const degree = record(data?.degree) ?? data;
  const progress = record(data?.progress);
  const requirements = list(data, ['requirements']);
  if (!degree && !progress) return null;
  const name = text(degree?.name) || 'Degree progress';
  const percent = typeof progress?.percentage === 'number' ? `${progress.percentage}% complete` : '';
  const counts = typeof progress?.completed === 'number' && typeof progress?.total === 'number' ? `${progress.completed} of ${progress.total} requirements` : '';
  return (
    <ResultCard eyebrow="Advisory degree progress" title={name} href="/degree-planning" hrefLabel="Open planner">
      <ResultRow icon={<AcademicCapIcon className="size-5" aria-hidden="true" />} title={percent || 'Progress available'} detail={[counts, text(degree?.code), 'Not an official degree audit'].filter(Boolean).join(' · ')} />
      {requirements.slice(0, 4).map((requirement, index) => (
        <ResultRow key={String(requirement.name ?? index)} icon={<AcademicCapIcon className="size-4" aria-hidden="true" />} title={text(requirement.name) || 'Requirement'} detail={text(requirement.status)} />
      ))}
    </ResultCard>
  );
}

export function AssistantDegreePlanVisual({ data }: { data: Record<string, unknown> | null }) {
  if (!data) return null;
  const plan = record(data.plan) ?? data;
  const semesters = list(plan, ['semesters', 'terms']);
  const name = text(data.name) || text(plan.name) || 'Selected degree plan';
  return (
    <ResultCard eyebrow="Saved degree plan" title={name} href="/degree-planning" hrefLabel="Open planner">
      <ResultRow
        icon={<AcademicCapIcon className="size-5" aria-hidden="true" />}
        title={semesters.length ? `${semesters.length} semester${semesters.length === 1 ? '' : 's'}` : 'Plan loaded'}
        detail="Owner-scoped saved plan · Open the planner for the editable roadmap."
      />
    </ResultCard>
  );
}

export function AssistantTransitVisual({ data }: { data: Record<string, unknown> | null }) {
  if (!data) return null;
  const stop = record(data.stop);
  const arrivals = list(data, ['arrivals']);
  const stopName = text(stop?.name) || text(data.requested_stop) || 'Selected stop';
  return (
    <ResultCard eyebrow="Live bus arrivals" title={stopName} href="/bus-tracker" hrefLabel="Open Bus Tracker">
      {arrivals.length ? arrivals.slice(0, 6).map((arrival, index) => {
        const minutes = arrival.arrivalMinutes;
        const eta = typeof minutes === 'number' && Number.isFinite(minutes) ? `${minutes} min` : 'ETA unavailable';
        return (
          <ResultRow
            key={String(arrival.vehicleId ?? arrival.routeName ?? index)}
            icon={<TruckIcon className="size-5" aria-hidden="true" />}
            title={text(arrival.routeName) || text(arrival.routeId) || 'Route'}
            detail={eta}
          />
        );
      }) : <ResultRow icon={<TruckIcon className="size-5" aria-hidden="true" />} title="No current arrivals" detail="Live Rutgers transit data returned no ETAs for this stop." />}
    </ResultCard>
  );
}

export function AssistantDiningVisual({ data }: { data: Record<string, unknown> | null }) {
  const halls = list(data, ['halls']);
  if (!halls.length) return null;
  return (
    <ResultCard eyebrow="Dining menus" title="What you can eat now" href="/dining" hrefLabel="Open Dining">
      {halls.slice(0, 4).map((hall) => {
        const items = Array.isArray(hall.featuredItems) ? hall.featuredItems as Array<Record<string, unknown>> : [];
        return (
          <ResultRow
            key={text(hall.hall) || text(hall.hallLabel)}
            icon={<MapPinIcon className="size-5" aria-hidden="true" />}
            title={text(hall.hallLabel) || text(hall.hall) || 'Dining hall'}
            detail={[text(hall.meal), items.slice(0, 3).map((item) => text(item.name)).filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
          />
        );
      })}
    </ResultCard>
  );
}

export function AssistantEventVisual({ data }: { data: Record<string, unknown> | null }) {
  const events = list(data, ['events']);
  if (!events.length) return null;
  return (
    <ResultCard eyebrow="Campus events" title={`${events.length} event${events.length === 1 ? '' : 's'}`} href="/events" hrefLabel="Open Events">
      {events.slice(0, 5).map((event, index) => (
        <ResultRow key={String(event.id ?? index)} icon={<CalendarDaysIcon className="size-5" aria-hidden="true" />} title={text(event.title) || text(event.name) || 'Event'} detail={[text(event.start), text(event.location), text(event.category)].filter(Boolean).join(' · ')} />
      ))}
    </ResultCard>
  );
}

export function AssistantParkingVisual({ data }: { data: Record<string, unknown> | null }) {
  const lots = list(data, ['lots']);
  if (!lots.length) return null;
  return (
    <ResultCard eyebrow="Permit access windows" title="Parking lots for today" href="/parking" hrefLabel="Open Parking">
      {lots.slice(0, 5).map((lot, index) => (
        <ResultRow key={String(lot.id ?? lot.name ?? index)} icon={<BuildingOffice2Icon className="size-5" aria-hidden="true" />} title={text(lot.name) || 'Lot'} detail={[text(lot.campus), text(lot.permit), 'Published rules, not live occupancy'].filter(Boolean).join(' · ')} />
      ))}
    </ResultCard>
  );
}

export function AssistantRoomVisual({ data }: { data: Record<string, unknown> | null }) {
  const rooms = list(data, ['rooms']);
  if (!rooms.length) return null;
  return (
    <ResultCard eyebrow="Checked room schedules" title={`${rooms.length} room${rooms.length === 1 ? '' : 's'}`} href="/rooms" hrefLabel="Open rooms">
      {rooms.slice(0, 5).map((room, index) => (
        <ResultRow key={String(room.id ?? room.name ?? index)} icon={<BuildingOffice2Icon className="size-5" aria-hidden="true" />} title={text(room.name) || [text(room.building), text(room.room)].filter(Boolean).join(' ') || 'Room'} detail={[text(room.campus), 'Not live occupancy'].filter(Boolean).join(' · ')} />
      ))}
    </ResultCard>
  );
}

export function AssistantProfessorVisual({ data }: { data: Record<string, unknown> | null }) {
  const professors = list(data, ['professors', 'instructors']);
  if (!professors.length) return null;
  return (
    <ResultCard eyebrow="Professors" title={`${professors.length} match${professors.length === 1 ? '' : 'es'}`} href="/explore?view=professors" hrefLabel="Open professors">
      {professors.slice(0, 5).map((professor, index) => (
        <ResultRow key={String(professor.id ?? professor.name ?? index)} icon={<UserGroupIcon className="size-5" aria-hidden="true" />} title={text(professor.name) || [text(professor.firstName), text(professor.lastName)].filter(Boolean).join(' ') || 'Instructor'} detail={[text(professor.department), 'Student ratings are perception signals'].filter(Boolean).join(' · ')} />
      ))}
    </ResultCard>
  );
}

export function AssistantResearchVisual({ data }: { data: Record<string, unknown> | null }) {
  const opportunities = list(data, ['opportunities', 'results']);
  if (!opportunities.length) return null;
  return (
    <ResultCard eyebrow="Research opportunities" title={`${opportunities.length} opening${opportunities.length === 1 ? '' : 's'}`} href="/research-opportunities" hrefLabel="Open research">
      {opportunities.slice(0, 5).map((opportunity, index) => (
        <ResultRow key={String(opportunity.id ?? index)} icon={<BeakerIcon className="size-5" aria-hidden="true" />} title={text(opportunity.title) || text(opportunity.name) || 'Opportunity'} detail={[text(opportunity.department), text(opportunity.campus)].filter(Boolean).join(' · ')} />
      ))}
    </ResultCard>
  );
}
